import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Stack,
  Alert,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { whitelistService } from '../../services/whitelistService';

const ipSchema = z.object({
  type: z.string(),
  ipAddress: z.string().optional(),
  ipPrefix: z.string().optional(),
  reason: z.string().min(1, 'Reason is required'),
  notes: z.string().optional(),
});

type FormData = z.infer<typeof ipSchema>;

interface AddWhitelistDialogProps {
  open: boolean;
  onClose: () => void;
}

export const AddWhitelistDialog: React.FC<AddWhitelistDialogProps> = ({ open, onClose }) => {
  const queryClient = useQueryClient();
  const [type, setType] = useState<'ip' | 'prefix'>('ip');
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(ipSchema),
    defaultValues: {
      type: 'ip',
      reason: 'trusted',
    },
  });

  const addIpMutation = useMutation({
    mutationFn: whitelistService.addIp,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whitelist'] });
      handleClose();
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to add IP to whitelist');
    },
  });

  const addPrefixMutation = useMutation({
    mutationFn: whitelistService.addPrefix,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whitelist'] });
      handleClose();
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to add prefix to whitelist');
    },
  });

  const handleClose = () => {
    reset();
    setError(null);
    onClose();
  };

  const onSubmit = (data: FormData) => {
    setError(null);

    if (type === 'ip') {
      if (!data.ipAddress) {
        setError('IP address is required');
        return;
      }
      addIpMutation.mutate({
        ipAddress: data.ipAddress,
        reason: data.reason,
        notes: data.notes,
      });
    } else {
      if (!data.ipPrefix) {
        setError('IP prefix is required');
        return;
      }
      addPrefixMutation.mutate({
        prefix: data.ipPrefix || '',
        reason: data.reason,
        notes: data.notes,
      });
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add to Whitelist</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Stack spacing={3}>
            {error && <Alert severity="error">{error}</Alert>}

            <FormControl>
              <FormLabel>Type</FormLabel>
              <RadioGroup
                row
                value={type}
                onChange={(e) => setType(e.target.value as 'ip' | 'prefix')}
              >
                <FormControlLabel value="ip" control={<Radio />} label="IP Address" />
                <FormControlLabel value="prefix" control={<Radio />} label="IP Prefix (CIDR)" />
              </RadioGroup>
            </FormControl>

            {type === 'ip' ? (
              <TextField
                label="IP Address"
                placeholder="192.168.1.100"
                {...register('ipAddress')}
                error={!!errors.ipAddress}
                helperText={errors.ipAddress?.message}
                fullWidth
                required
              />
            ) : (
              <TextField
                label="IP Prefix (CIDR)"
                placeholder="192.168.1.0/24"
                {...register('ipPrefix')}
                error={!!errors.ipPrefix}
                helperText={errors.ipPrefix?.message}
                fullWidth
                required
              />
            )}

            <TextField
              label="Reason"
              {...register('reason')}
              error={!!errors.reason}
              helperText={errors.reason?.message}
              fullWidth
              required
            />

            <TextField
              label="Notes"
              {...register('notes')}
              error={!!errors.notes}
              helperText={errors.notes?.message}
              fullWidth
              multiline
              rows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={addIpMutation.isPending || addPrefixMutation.isPending}
          >
            Add to Whitelist
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
